import { useMemo } from 'react'
import { defineMessage, useIntl } from 'react-intl'

import { BaseCountryItem } from '../../../models/user/User'
import { DEFAULT_COUNTRY_ISO_CODE } from '../../common/constants/constants'

const unitedStatesAndTerritoriesLabel = defineMessage({
  defaultMessage: 'United States and territories',
  description:
    'Country dropdown label shown in place of "United States" once US territories have been consolidated into US registration',
})

export function useLocalizedCountryList<T extends BaseCountryItem>(
  countryList: T[],
  options?: { relabelUSWithTerritories?: boolean },
): (T & { localizedCountryName: string; displayCountryName?: string })[] {
  const { formatDisplayName, formatMessage } = useIntl()
  const relabelUSWithTerritories = options?.relabelUSWithTerritories ?? false

  return useMemo(() => {
    const collator = new Intl.Collator()

    const sortedCountryList = countryList.map((country) => {
      const displayCountryName =
        relabelUSWithTerritories && country.countryIsoCode === DEFAULT_COUNTRY_ISO_CODE
          ? formatMessage(unitedStatesAndTerritoriesLabel)
          : undefined
      return {
        ...country,
        displayCountryName,
        localizedCountryName:
          displayCountryName ?? formatDisplayName(country.countryIsoCode, { type: 'region' }) ?? country.countryName,
      }
    })
    sortedCountryList.sort((a, b) => collator.compare(a.localizedCountryName, b.localizedCountryName))

    return sortedCountryList
  }, [countryList, formatDisplayName, formatMessage, relabelUSWithTerritories])
}
